<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">CLV 分析</h1>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="filter-group">
        <label>排序方式</label>
        <select v-model="filters.sortBy" class="form-control" @change="fetchData">
          <option value="predictedClv">預測 CLV</option>
          <option value="historicalClv">歷史 CLV</option>
          <option value="totalRevenue">總營收</option>
          <option value="clvRank">排名</option>
        </select>
      </div>
      <button class="btn btn-primary" @click="fetchData">查詢</button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <div class="error-actions">
        <button class="btn btn-secondary" @click="fetchData">重試</button>
        <button class="btn btn-primary" @click="triggerCalculation">
          <i class="ri-refresh-line"></i>
          執行 CLV 計算
        </button>
      </div>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>排名</th>
            <th>會員 ID</th>
            <th>歷史 CLV</th>
            <th>預測 CLV</th>
            <th>總營收</th>
            <th>訂單數</th>
            <th>平均客單價</th>
            <th>購買頻率</th>
            <th>流失機率</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="items.length === 0">
            <td colspan="9" class="empty-state">
              <p>沒有找到資料</p>
              <button class="btn btn-primary" @click="triggerCalculation">
                <i class="ri-refresh-line"></i>
                執行 CLV 計算
              </button>
            </td>
          </tr>
          <tr v-for="(item, index) in items" :key="item.id">
            <td>{{ item.clvRank || index + 1 }}</td>
            <td>{{ item.memberId }}</td>
            <td>{{ formatCurrency(item.historicalClv) }}</td>
            <td>{{ formatCurrency(item.predictedClv) }}</td>
            <td>{{ formatCurrency(item.totalRevenue) }}</td>
            <td>{{ item.totalOrders }}</td>
            <td>{{ formatCurrency(item.averageOrderValue) }}</td>
            <td>{{ formatPurchaseFrequency(item.purchaseFrequency) }}</td>
            <td>
              <span :class="getChurnProbabilityClass(item.churnProbability)">
                {{ item.churnProbability ? (item.churnProbability * 100).toFixed(1) + '%' : '-' }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="pagination"
      :page="pagination.page"
      :total-pages="pagination.totalPages"
      @change="goToPage"
    />
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { analyticsService } from '@/api/modules/analytics.js';
import { showError, showSuccess } from '@/utils/swal.js';

export default {
  name: 'ClvAnalysis',
  setup() {
    const loading = ref(false);
    const error = ref(null);
    const items = ref([]);
    const pagination = ref(null);
    const filters = ref({
      sortBy: 'predictedClv'
    });

    const fetchData = async () => {
      try {
        loading.value = true;
        error.value = null;
        const params = {
          page: pagination.value?.page || 1,
          pageSize: 20,
          ...filters.value
        };
        const response = await analyticsService.getClvRanking(params);
        if (response.success) {
          items.value = response.data.items || [];
          pagination.value = response.data.pagination;
          // 如果沒有資料，不顯示錯誤，而是顯示空狀態
          if (items.value.length === 0 && (!pagination.value || pagination.value.totalItems === 0)) {
            error.value = null; // 清除錯誤，讓空狀態顯示
          }
        } else {
          error.value = response.message || '取得資料失敗';
        }
      } catch (err) {
        console.error('CLV 分析錯誤:', err);
        console.error('錯誤詳情:', {
          response: err.response,
          data: err.response?.data,
          status: err.response?.status,
          message: err.message
        });
        
        // 顯示友善的錯誤訊息
        let errorMessage = '取得資料失敗';
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        // 如果是 404 或空資料，顯示更友善的訊息
        if (err.response?.status === 404 || errorMessage.includes('沒有資料') || errorMessage.includes('找不到')) {
          errorMessage = '目前沒有 CLV 分析資料，請先執行 CLV 計算';
        }
        
        error.value = errorMessage;
        showError('錯誤', errorMessage);
      } finally {
        loading.value = false;
      }
    };

    const goToPage = (page) => {
      if (pagination.value) {
        pagination.value.page = page;
        fetchData();
      }
    };

    const formatCurrency = (value) => {
      if (!value && value !== 0) return '-';
      return `$${parseInt(value).toLocaleString()}`;
    };

    const formatPurchaseFrequency = (value) => {
      if (value === null || value === undefined || value === '') return '-';
      const numValue = typeof value === 'number' ? value : parseFloat(value);
      if (isNaN(numValue)) return '-';
      return numValue.toFixed(2);
    };

    const getChurnProbabilityClass = (probability) => {
      if (!probability) return '';
      if (probability >= 0.7) return 'badge badge-danger';
      if (probability >= 0.4) return 'badge badge-warning';
      return 'badge badge-success';
    };

    const triggerCalculation = async () => {
      try {
        loading.value = true;
        error.value = null;
        const response = await analyticsService.triggerClvCalculation();
        if (response.success) {
          const data = response.data;
          let successMessage = data.message || 'CLV 計算完成';
          
          // 顯示詳細資訊
          if (data.totalMembers > 0) {
            successMessage += `\n\n統計資訊：\n- 找到 ${data.totalMembers} 位有訂單的會員\n- 成功計算 ${data.successCount} 位\n- 失敗 ${data.failCount} 位`;
            if (data.errors && data.errors.length > 0) {
              successMessage += `\n\n失敗原因：\n${data.errors.map(e => `- 會員 ${e.memberId}: ${e.error}`).join('\n')}`;
            }
          } else {
            successMessage = '目前沒有會員有訂單記錄，無法進行 CLV 計算。請先建立訂單資料。';
          }
          
          showSuccess('成功', successMessage);
          // 等待 2 秒後重新載入資料
          setTimeout(() => {
            fetchData();
          }, 2000);
        } else {
          error.value = response.message || 'CLV 計算失敗';
          showError('錯誤', error.value);
        }
      } catch (err) {
        console.error('CLV 計算錯誤:', err);
        error.value = err.response?.data?.message || err.message || 'CLV 計算失敗';
        showError('錯誤', error.value);
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      fetchData();
    });

    return {
      loading,
      error,
      items,
      pagination,
      filters,
      fetchData,
      goToPage,
      formatCurrency,
      formatPurchaseFrequency,
      getChurnProbabilityClass,
      triggerCalculation
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.error-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 16px;
}
</style>
